import { memo, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { API } from '@/api'
import {
  ServerPrivateRegionNodeDetails,
  ServerPrivateRegionNodeSpec,
  ServerPrivateRegionVGpuSpec,
} from '@/api/type-server'
import { Button, Modal } from '@/components/base'
import { message } from '@/components/base/Message'

import { calculateResourceUsage, specToInput } from '../utils/specHelpers'
import { ReadOnlySpecSection } from './ReadOnlySpecSection'
import { ResourceUsageCard } from './ResourceUsageCard'

interface InstanceSpecPreviewModalProps {
  open: boolean
  node: ServerPrivateRegionNodeDetails
  specs: ServerPrivateRegionNodeSpec[]
  vgpuSpecs?: ServerPrivateRegionVGpuSpec[]
  onClose: () => void
  onPrevious: () => void
  onSaveSuccess: () => void
}

export const InstanceSpecPreviewModal = memo(function InstanceSpecPreviewModal({
  open,
  node,
  specs,
  vgpuSpecs,
  onClose,
  onPrevious,
  onSaveSuccess,
}: InstanceSpecPreviewModalProps) {
  const { t } = useTranslation('privateRegion')
  const [loading, setLoading] = useState(false)

  // Calculate resource usage
  const resourceUsage = useMemo(
    () => calculateResourceUsage(specs, node, vgpuSpecs),
    [specs, node, vgpuSpecs],
  )

  // Handle save configuration
  const handleSave = useCallback(async () => {
    setLoading(true)

    try {
      const response = await API.updatePrivateRegionSpecs({
        specs: specs.map(specToInput),
      })

      if (response.code !== 0) {
        message.error(t('errors.failedToSave'))
        return
      }

      message.success(t('editInstanceSpec.saveSuccess'))
      onSaveSuccess()
    } catch (err) {
      message.error(t('errors.failedToSave'))
    } finally {
      setLoading(false)
    }
  }, [specs, onSaveSuccess, t])

  return (
    <Modal open={open} onClose={onClose} className="w-[1344px]">
      <Modal.Header>{t('instanceSpecPreview.title')}</Modal.Header>

      <Modal.Body className="flex flex-col gap-4 max-h-[calc(90vh-180px)]">
        {/* Fixed section - Resource Usage Cards */}
        <div className="flex-shrink-0 flex gap-3">
          {/* Physical GPU: vGPU (cyan) + GPU Passthrough (blue) */}
          <ResourceUsageCard
            title={t('editInstanceSpec.physicalGpuUsage')}
            vgpuUsed={resourceUsage.gpu.vgpuUsed}
            gpuUsed={resourceUsage.gpu.gpuUsed}
            total={resourceUsage.gpu.total}
            unit="GPU"
          />
          {/* vCPU: vGPU spec (cyan) + GPU spec (blue) + vCPU spec (green) */}
          <ResourceUsageCard
            title={t('editInstanceSpec.vcpuUsage')}
            vgpuSpecUsed={resourceUsage.cpu.vgpuSpecUsed}
            gpuSpecUsed={resourceUsage.cpu.gpuSpecUsed}
            cpuSpecUsed={resourceUsage.cpu.cpuSpecUsed}
            total={resourceUsage.cpu.total}
            unit="Cores"
          />
          {/* Memory: vGPU spec (cyan) + GPU spec (blue) + vCPU spec (green) */}
          <ResourceUsageCard
            title={t('editInstanceSpec.memoryUsage')}
            vgpuSpecUsed={resourceUsage.ram.vgpuSpecUsed}
            gpuSpecUsed={resourceUsage.ram.gpuSpecUsed}
            cpuSpecUsed={resourceUsage.ram.cpuSpecUsed}
            total={resourceUsage.ram.total}
            unit="GB"
          />
          {/* Disk: vGPU spec (cyan) + GPU spec (blue) + vCPU spec (green) */}
          <ResourceUsageCard
            title={t('editInstanceSpec.diskUsage')}
            vgpuSpecUsed={resourceUsage.disk.vgpuSpecUsed}
            gpuSpecUsed={resourceUsage.disk.gpuSpecUsed}
            cpuSpecUsed={resourceUsage.disk.cpuSpecUsed}
            total={resourceUsage.disk.total}
            unit="GB"
          />
        </div>

        {/* Scrollable section - Read-only Spec Sections */}
        <div className="flex-1 overflow-y-auto pr-2 min-h-0">
          <ReadOnlySpecSection specs={specs} vgpuSpecs={vgpuSpecs} />
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button type="outline" onClick={onPrevious} disabled={loading}>
          {t('editInstanceSpec.previous')}
        </Button>
        <Button type="secondary" onClick={handleSave} disabled={loading}>
          {loading
            ? t('common.loading', { ns: 'translation' })
            : t('editInstanceSpec.saveConfiguration')}
        </Button>
      </Modal.Footer>
    </Modal>
  )
})
